import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaStar, FaRegStar } from 'react-icons/fa';
import { API } from '@unboared/react';

import { FeedbackService } from '~/services/feedback/FeedbackService';

import styles from './GameRating.module.css';

const COMMENT_THRESHOLD = 3; // ratings <= 3 also ask for a written follow-up

const safe = <T,>(fn: () => T): T | undefined => {
    try {
        return fn();
    } catch {
        return undefined;
    }
};

const ratedKey = (sessionID?: string) => `unboared::feedback::${sessionID ?? 'unknown'}`;

/**
 * Note de satisfaction du joueur (1 à 5 étoiles + commentaire si note basse), écrite
 * dans `userFeedback` (avis du dashboard super-admin). Partagée par l'écran entre deux
 * jeux (InterGameScreen) et l'attente du double écran (PhoneWaitingScreen) : même clé
 * de session, donc un joueur n'est sollicité qu'une fois par session, où qu'il note.
 *
 * Ne rend rien sans jeu joué (`gameID`) ou si le joueur a déjà noté.
 */
export function GameRating({
    gameID,
    clientID,
    className,
    submitPending = false,
    onCommentingChange,
}: {
    gameID?: string;
    clientID?: string;
    className?: string;
    /**
     * Le jeu démarre (double écran) : les étoiles déjà choisies partent sans attendre
     * « Envoyer », puis la notation se retire. Volontairement PAS au démontage : celui-ci
     * arrive aussi sur un hoquet réseau, et la note partirait à moitié écrite.
     */
    submitPending?: boolean;
    /** Prévient le parent quand le champ de commentaire est ouvert (clavier, place). */
    onCommentingChange?: (commenting: boolean) => void;
}) {
    const { t } = useTranslation();
    const sessionID = safe(() => API.getSessionID());

    // Throttle: ask at most once per device per session. The gamepad app keeps a
    // stable origin across menu/game navigation, so sessionStorage survives.
    const alreadyRated = useMemo(() => {
        try {
            return sessionStorage.getItem(ratedKey(sessionID)) === '1';
        } catch {
            return false;
        }
    }, [sessionID]);

    const [selected, setSelected] = useState(0);
    const [comment, setComment] = useState('');
    const [submitted, setSubmitted] = useState(false);
    // Synchronous guard against a double-tap on "Envoyer" before the re-render
    // that unmounts the form (a state flag would still be false on the 2nd call).
    const sentRef = useRef(false);

    const canRate = Boolean(gameID) && !alreadyRated;

    // No hover preview: the gamepad is a touch device, and synthetic hover events
    // are unreliable on phones (sticky `mouseenter` with no `mouseleave`), which
    // could light stars the player never actually selected. Display = selection.
    const wantsComment = selected > 0 && selected <= COMMENT_THRESHOLD;
    const commenting = canRate && wantsComment && !submitted;

    const onCommentingRef = useRef(onCommentingChange);
    onCommentingRef.current = onCommentingChange;
    useEffect(() => {
        onCommentingRef.current?.(commenting);
    }, [commenting]);

    const send = (rating: number, text: string) => {
        if (sentRef.current || rating === 0) return;
        sentRef.current = true;
        try {
            sessionStorage.setItem(ratedKey(sessionID), '1');
        } catch {
            /* sessionStorage unavailable (Safari private) — best effort */
        }
        if (clientID) {
            FeedbackService.saveFeedback(clientID, {
                rating,
                comment: rating <= COMMENT_THRESHOLD ? text.trim() || undefined : undefined,
                gameID,
                deviceID: safe(() => API.getDeviceID()),
                uid: safe(() => API.getUID()),
                sessionID,
                mode: safe(() => API.getMode()),
            }).catch((error) => {
                // Never block the player on a feedback write — just log it.
                // eslint-disable-next-line no-console
                console.error('[feedback] save failed', error);
            });
        } else {
            // Should not happen (clientID comes from the QR URL) — but make the
            // dropped write visible rather than silently losing the rating.
            // eslint-disable-next-line no-console
            console.warn('[feedback] no clientID, rating not saved', { rating });
        }
    };

    const [gone, setGone] = useState(false);
    const latest = useRef({ selected, comment, send });
    latest.current = { selected, comment, send };
    useEffect(() => {
        if (!submitPending) return;
        const { selected: s, comment: c, send: doSend } = latest.current;
        if (s > 0) doSend(s, c);
        // Notée (ici ou avec « Envoyer ») : plus rien à montrer au prochain accueil.
        if (sentRef.current) setGone(true);
    }, [submitPending]);

    if (!canRate || gone) return null;

    if (submitted) {
        return (
            <section className={`${styles.thanks} ${className ?? ''}`}>
                <span className={styles.thanksEmoji} aria-hidden="true">
                    🙌
                </span>
                <p className={styles.thanksText}>{t('gamepad.interGame.thanks')}</p>
            </section>
        );
    }

    return (
        <section className={`${styles.rate} ${className ?? ''}`}>
            <p className={styles.prompt}>{t('gamepad.interGame.ratePrompt')}</p>

            <div className={styles.stars} role="radiogroup" aria-label={t('gamepad.interGame.ratePrompt')}>
                {[1, 2, 3, 4, 5].map((n) => (
                    <button
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={selected === n}
                        aria-label={`${n}`}
                        className={`${styles.starBtn} ${n <= selected ? styles.lit : ''}`}
                        style={{ '--i': n - 1 } as React.CSSProperties}
                        onClick={() => setSelected(n)}
                    >
                        {n <= selected ? <FaStar /> : <FaRegStar />}
                    </button>
                ))}
            </div>

            <p className={`${styles.caption} ${selected ? styles.captionShown : ''}`}>
                {selected ? t(`gamepad.interGame.ratings.${selected}`) : ' '}
            </p>

            {wantsComment && (
                <div className={styles.commentWrap}>
                    <textarea
                        className={styles.textarea}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={t('gamepad.interGame.commentPlaceholder')}
                        rows={3}
                        maxLength={500}
                    />
                </div>
            )}

            {selected > 0 && (
                <button
                    type="button"
                    className={styles.cta}
                    onClick={() => {
                        send(selected, comment);
                        setSubmitted(true);
                    }}
                >
                    {t('gamepad.interGame.send')}
                </button>
            )}
        </section>
    );
}
